import { SlipEntry } from 'types/SlipEntry';

/** The bonus on the number of matches, as the server sends it. */
export interface MultiBonusTier {
  matches: number;
  percent: number;
}

export interface MultiBonusRules {
  enabled: boolean;
  min_odds?: number;
  /** Most bonus one ticket gets; 0 means no cap of its own. */
  max_bonus?: number;
  tiers?: MultiBonusTier[];
}

/**
 * Matches that earn the bonus, counted the way the server counts them: every
 * pick of the match going on the ticket has at least the least odds.
 */
export const multiBonusMatches = (entries: SlipEntry[], minOdds: number) =>
  entries.filter((e) => {
    const picks = e.bets.filter((b) => !b.disabled);
    return (
      picks.length > 0 && picks.every((b) => Number(b.odd.odds) >= minOdds)
    );
  }).length;

const sortedTiers = (tiers: MultiBonusTier[] = []) =>
  [...tiers].sort((a, b) => a.matches - b.matches);

/** The percentage for n matches: the highest tier reached, else 0. */
export const multiBonusPercent = (tiers: MultiBonusTier[] = [], n: number) => {
  let pct = 0;
  for (const t of sortedTiers(tiers)) if (n >= t.matches) pct = t.percent;
  return pct;
};

/** The next tier above n matches, to tell the player what one more brings. */
export const nextMultiBonusTier = (tiers: MultiBonusTier[] = [], n: number) =>
  sortedTiers(tiers).find((t) => t.matches > n && t.percent > 0) ?? null;

/**
 * The bonus on a win, as the server pays it: the percentage of it rounded
 * down to the cent, within the ticket's own cap and the ticket maximum.
 */
export const multiBonusAmount = (
  win: number,
  percent: number,
  maxBonus: number,
  maxWin: number
) => {
  if (!(win > 0) || !(percent > 0)) return 0;
  // win x percent / 100, rounded down; the nudge keeps 28.999... at 29.
  let bonus = Math.floor(win * percent + 1e-6) / 100;
  if (maxBonus > 0) bonus = Math.min(bonus, maxBonus);
  return Math.max(0, Math.min(bonus, maxWin - win));
};
